import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { readMandateSession } from "../../util/mandateSessionStorage";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";
import { showSnackBar } from "./snackSlice";

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const getSwitchedClinicIdQuery = (params) => {
  let clinicId = params?.clinicId ?? null;
  if (clinicId == null || clinicId === "") {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      clinicId = user?.activeBranchId ?? null;
    } catch {
      clinicId = null;
    }
  }
  return clinicId != null && clinicId !== "" ? `?clinicId=${clinicId}` : "";
};

const getGlobalSettingsDataParam = () => {
  const globalSettingsData =
    readMandateSession()?.dataSharing?.globalSettingsData ?? false;
  return `globalSettingsData=${globalSettingsData}`;
};

const getApprovalRulesQuery = (params) => {
  const clinicIdQuery = getSwitchedClinicIdQuery(params);
  const settingsParam = getGlobalSettingsDataParam();
  return clinicIdQuery
    ? `${clinicIdQuery}&${settingsParam}`
    : `?${settingsParam}`;
};

export const getApprovalRules = createAsyncThunk(
  "globalSettings/getApprovalRules",
  async (params, thunkAPI) => {
    const silent = !!params?.silent;
    const rulesQuery = getApprovalRulesQuery(params);
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/approval-rules${rulesQuery}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        const payload = res?.data?.data ?? res?.data ?? [];
        return payload;
      }
      return thunkAPI.rejectWithValue("Failed to fetch approval rules");
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(err, "Failed to fetch approval rules"),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch approval rules"),
      );
    }
  },
);

export const createApprovalRequest = createAsyncThunk(
  "globalSettings/createApprovalRequest",
  async (payload, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/approvals`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        return res?.data?.data ?? res?.data ?? null;
      }
      return thunkAPI.rejectWithValue("Failed to create approval request");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to create approval request"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to create approval request"),
      );
    }
  },
);

export const getApprovals = createAsyncThunk(
  "globalSettings/getApprovals",
  async (params, thunkAPI) => {
    const silent = !!params?.silent;
    const clinicIdQuery = getSwitchedClinicIdQuery(params);
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/approvals${clinicIdQuery}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        const payload = res?.data?.data ?? res?.data ?? {};
        return {
          clinicId: payload?.clinicId ?? null,
          count: payload?.count ?? 0,
          approvals: Array.isArray(payload?.approvals) ? payload.approvals : [],
        };
      }
      return thunkAPI.rejectWithValue("Failed to fetch approvals");
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(err, "Failed to fetch approvals"),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch approvals"),
      );
    }
  },
);

export const approveApprovalRequest = createAsyncThunk(
  "globalSettings/approveApprovalRequest",
  async ({ approvalId, remarks }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/approvals/${approvalId}/approve`,
        { remarks: remarks ?? "" },
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Request status updated", // res?.data?.message || "Approved successfully",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? { approvalId };
      }
      return thunkAPI.rejectWithValue("Failed to approve request");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to approve request"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to approve request"),
      );
    }
  },
);

export const updateApprovalReturnId = createAsyncThunk(
  "globalSettings/updateApprovalReturnId",
  async ({ approvalId, returnId }, thunkAPI) => {
    try {
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/approvals/${approvalId}/return-id`,
        { returnId },
      );
      if (res.status === 200 || res.status === 201) {
        return res?.data?.data ?? res?.data ?? { approvalId, returnId };
      }
      return thunkAPI.rejectWithValue("Failed to update return id");
    } catch (err) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update return id"),
      );
    }
  },
);

export const rejectApprovalRequest = createAsyncThunk(
  "globalSettings/rejectApprovalRequest",
  async ({ approvalId, remarks }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/approvals/${approvalId}/reject`,
        { remarks: remarks ?? "" },
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Request status updated", // res?.data?.message || "Rejected successfully",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? { approvalId };
      }
      return thunkAPI.rejectWithValue("Failed to reject request");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to reject request"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to reject request"),
      );
    }
  },
);

export const createApprovalRules = createAsyncThunk(
  "globalSettings/createApprovalRules",
  async (payload, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/approval-rules`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? payload;
      }
      return thunkAPI.rejectWithValue("Failed to create approval rules");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to create approval rules"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to create approval rules"),
      );
    }
  },
);

export const updateApprovalRules = createAsyncThunk(
  "globalSettings/updateApprovalRules",
  async (payload, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/approval-rules`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? payload;
      }
      return thunkAPI.rejectWithValue("Failed to update approval rules");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to update approval rules"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update approval rules"),
      );
    }
  },
);

export const createApprovalRuleTiers = createAsyncThunk(
  "globalSettings/createApprovalRuleTiers",
  async (
    { ruleType = "REFUND", autoApproval = false, rules = [] },
    thunkAPI,
  ) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/approval-rules/${ruleType}/tiers`,
        {
          autoApproval: !!autoApproval,
          rules: autoApproval ? [] : rules,
        },
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Approval tiers created successfully",
            type: "success",
          }),
        );
        return {
          ruleType,
          data: res?.data?.data ?? res?.data ?? { autoApproval, rules },
        };
      }
      return thunkAPI.rejectWithValue("Failed to create approval tiers");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to create approval tiers"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to create approval tiers"),
      );
    }
  },
);

export const updateApprovalRuleTiers = createAsyncThunk(
  "globalSettings/updateApprovalRuleTiers",
  async (
    { ruleType = "REFUND", autoApproval = false, rules = [] },
    thunkAPI,
  ) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/approval-rules/tiers`,
        {
          autoApproval: !!autoApproval,
          ruleType,
          rules: autoApproval ? [] : rules,
        },
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Approval tiers updated successfully",
            type: "success",
          }),
        );
        return (
          res?.data?.data ?? res?.data ?? { autoApproval, ruleType, rules }
        );
      }
      return thunkAPI.rejectWithValue("Failed to update approval tiers");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to update approval tiers"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update approval tiers"),
      );
    }
  },
);

export const deleteApprovalRuleTier = createAsyncThunk(
  "globalSettings/deleteApprovalRuleTier",
  async (payload, thunkAPI) => {
    const tierId =
      typeof payload === "object" && payload != null ? payload.tierId : payload;
    const silent =
      typeof payload === "object" && payload != null ? !!payload.silent : false;
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/clinic/approval-rules/tiers/${tierId}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 204) {
        if (!silent) {
          thunkAPI.dispatch(
            showSnackBar({
              message: "Approval tier deleted successfully",
              type: "success",
            }),
          );
        }
        return tierId;
      }
      return thunkAPI.rejectWithValue("Failed to delete approval tier");
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(err, "Failed to delete approval tier"),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to delete approval tier"),
      );
    }
  },
);

export const getEmrSettings = createAsyncThunk(
  "globalSettings/getEmrSettings",
  async (params, thunkAPI) => {
    const silent = !!params?.silent;
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/emr-settings?${getGlobalSettingsDataParam()}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        return res?.data?.data ?? res?.data ?? null;
      }
      return thunkAPI.rejectWithValue("Failed to fetch EMR settings");
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(err, "Failed to fetch EMR settings"),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch EMR settings"),
      );
    }
  },
);

export const updateEmrSettings = createAsyncThunk(
  "globalSettings/updateEmrSettings",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/emr-settings`,
        data,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? data;
      }
      return thunkAPI.rejectWithValue("Failed to update EMR settings");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(err, "Failed to update EMR settings"),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update EMR settings"),
      );
    }
  },
);

export const getBillingPreviewSettings = createAsyncThunk(
  "globalSettings/getBillingPreviewSettings",
  async (params, thunkAPI) => {
    const silent = !!params?.silent;
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/billing-preview-settings?${getGlobalSettingsDataParam()}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        return res?.data?.data ?? res?.data ?? null;
      }
      return thunkAPI.rejectWithValue(
        "Failed to fetch billing preview settings",
      );
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(
              err,
              "Failed to fetch billing preview settings",
            ),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch billing preview settings"),
      );
    }
  },
);

export const updateBillingPreviewSettings = createAsyncThunk(
  "globalSettings/updateBillingPreviewSettings",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/billing-preview-settings`,
        data,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? data;
      }
      return thunkAPI.rejectWithValue(
        "Failed to update billing preview settings",
      );
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(
            err,
            "Failed to update billing preview settings",
          ),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update billing preview settings"),
      );
    }
  },
);

export const getRoleModulePermissions = createAsyncThunk(
  "globalSettings/getRoleModulePermissions",
  async (params, thunkAPI) => {
    const silent = !!params?.silent;
    try {
      if (!silent) thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/role-module-permissions?${getGlobalSettingsDataParam()}`,
      );
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (res.status === 200) {
        const payload = res?.data?.data ?? res?.data ?? {};
        return {
          clinicId: payload?.clinicId ?? null,
          rolePermissions: Array.isArray(payload?.rolePermissions)
            ? payload.rolePermissions
            : [],
        };
      }
      return thunkAPI.rejectWithValue(
        "Failed to fetch role module permissions",
      );
    } catch (err) {
      if (!silent) thunkAPI.dispatch(hideLoader());
      if (!silent) {
        thunkAPI.dispatch(
          showSnackBar({
            message: getErrorMessage(
              err,
              "Failed to fetch role module permissions",
            ),
            type: "error",
          }),
        );
      }
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to fetch role module permissions"),
      );
    }
  },
);

export const getRoleModulePermissionsWoLoader = createAsyncThunk(
  "globalSettings/getRoleModulePermissionsWoLoader",
  async (url = "", thunkAPI) => {
    const settingsParam = getGlobalSettingsDataParam();
    const query = url
      ? `${url}${url.includes("?") ? "&" : "?"}${settingsParam}`
      : `?${settingsParam}`;
    try {
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/role-module-permissions${query}`,
      );
      if (res?.status === 200) {
        const payload = res?.data?.data ?? res?.data ?? {};
        return {
          clinicId: payload?.clinicId ?? null,
          rolePermissions: Array.isArray(payload?.rolePermissions)
            ? payload.rolePermissions
            : [],
        };
      }
    } catch (err) {
      // silent — mandate prefetch
    }
  },
);

export const createRoleModulePermissions = createAsyncThunk(
  "globalSettings/createRoleModulePermissions",
  async (payload, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/role-module-permissions`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            // res?.data?.message ||
            // "Role module permissions created successfully",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? payload;
      }
      return thunkAPI.rejectWithValue(
        "Failed to create role module permissions",
      );
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(
            err,
            "Failed to create role module permissions",
          ),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to create role module permissions"),
      );
    }
  },
);

export const updateRoleModulePermissions = createAsyncThunk(
  "globalSettings/updateRoleModulePermissions",
  async (payload, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/clinic/role-module-permissions`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            // res?.data?.message ||
            // "Role module permissions updated successfully",
            type: "success",
          }),
        );
        return res?.data?.data ?? res?.data ?? payload;
      }
      return thunkAPI.rejectWithValue(
        "Failed to update role module permissions",
      );
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({
          message: getErrorMessage(
            err,
            "Failed to update role module permissions",
          ),
          type: "error",
        }),
      );
      return thunkAPI.rejectWithValue(
        getErrorMessage(err, "Failed to update role module permissions"),
      );
    }
  },
);

const initialState = {
  approvalRules: null,
  approvals: [],
  approvalsCount: 0,
  availableRoles: [],
  orgPolicies: [],
  ruleTypes: [],
  emrSettings: null,
  billingPreviewSettings: null,
  roleModulePermissions: {
    clinicId: null,
    rolePermissions: [],
  },
  approvalTiersByType: {
    REFUND: [],
    WRITE_OFF: [],
    DISCOUNT_PERCENT: [],
    RETURN: [],
  },
  loading: false,
  error: null,
};

const normalizeApprovalRules = (payload) => {
  if (payload?.rules && typeof payload.rules === "object") {
    const byType = {};
    Object.entries(payload.rules).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        byType[key] = value;
      } else if (value && typeof value === "object") {
        byType[key] = Array.isArray(value?.rules) ? value.rules : [];
      }
    });
    return byType;
  }

  const byType = {};

  if (Array.isArray(payload)) {
    payload.forEach((item) => {
      const ruleType =
        item?.ruleType || item?.type || item?.approvalType || item?.name;
      if (ruleType && Array.isArray(item?.tiers)) {
        byType[ruleType] = item.tiers;
        return;
      }
      if (ruleType && Array.isArray(item?.rules)) {
        byType[ruleType] = item.rules;
        return;
      }
      const tierType = item?.ruleType || item?.type || item?.approvalType;
      if (
        tierType &&
        (item?.minValue != null || item?.tierId != null || item?.id != null)
      ) {
        if (!byType[tierType]) byType[tierType] = [];
        byType[tierType].push(item);
      }
    });
    return byType;
  }

  if (payload && typeof payload === "object") {
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        byType[key] = value;
      } else if (Array.isArray(value?.tiers)) {
        byType[key] = value.tiers;
      } else if (Array.isArray(value?.rules)) {
        byType[key] = value.rules;
      }
    });
  }

  return byType;
};

const globalSettingsSlice = createSlice({
  name: "globalSettings",
  initialState,
  reducers: {
    clearApprovalRuleTiers: (state, action) => {
      const ruleType = action.payload || "REFUND";
      state.approvalTiersByType[ruleType] = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApprovalRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getApprovalRules.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.approvalRules = payload;
        state.availableRoles = Array.isArray(payload?.availableRoles)
          ? payload.availableRoles
          : [];
        state.orgPolicies = Array.isArray(payload?.orgPolicies)
          ? payload.orgPolicies
          : [];
        state.ruleTypes = Array.isArray(payload?.ruleTypes)
          ? payload.ruleTypes
          : [];
        state.approvalTiersByType = {
          ...state.approvalTiersByType,
          ...normalizeApprovalRules(payload),
        };
      })
      .addCase(getApprovalRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getApprovals.fulfilled, (state, action) => {
        state.approvals = Array.isArray(action.payload?.approvals)
          ? action.payload.approvals
          : [];
        state.approvalsCount = Number(action.payload?.count ?? 0);
      })
      .addCase(getApprovals.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createApprovalRuleTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApprovalRuleTiers.fulfilled, (state, action) => {
        state.loading = false;
        const { ruleType, data } = action.payload || {};
        const key = ruleType || "REFUND";
        if (Array.isArray(data)) {
          state.approvalTiersByType[key] = data;
        } else if (Array.isArray(data?.rules)) {
          state.approvalTiersByType[key] = data.rules;
        }
      })
      .addCase(createApprovalRuleTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateApprovalRuleTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateApprovalRuleTiers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateApprovalRuleTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteApprovalRuleTier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteApprovalRuleTier.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        Object.keys(state.approvalTiersByType).forEach((key) => {
          state.approvalTiersByType[key] = (
            state.approvalTiersByType[key] || []
          ).filter(
            (tier) => tier?.id !== deletedId && tier?.tierId !== deletedId,
          );
        });
      })
      .addCase(deleteApprovalRuleTier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEmrSettings.fulfilled, (state, action) => {
        state.emrSettings = action.payload || null;
      })
      .addCase(getEmrSettings.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateEmrSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmrSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.emrSettings = action.payload || state.emrSettings;
      })
      .addCase(updateEmrSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getBillingPreviewSettings.fulfilled, (state, action) => {
        state.billingPreviewSettings = action.payload || null;
      })
      .addCase(getBillingPreviewSettings.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateBillingPreviewSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBillingPreviewSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.billingPreviewSettings =
          action.payload || state.billingPreviewSettings;
      })
      .addCase(updateBillingPreviewSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getRoleModulePermissions.fulfilled, (state, action) => {
        state.roleModulePermissions = {
          clinicId: action.payload?.clinicId ?? null,
          rolePermissions: Array.isArray(action.payload?.rolePermissions)
            ? action.payload.rolePermissions
            : [],
        };
      })
      .addCase(getRoleModulePermissions.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getRoleModulePermissionsWoLoader.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.roleModulePermissions = {
          clinicId: action.payload?.clinicId ?? null,
          rolePermissions: Array.isArray(action.payload?.rolePermissions)
            ? action.payload.rolePermissions
            : [],
        };
      })
      .addCase(createRoleModulePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRoleModulePermissions.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload && typeof payload === "object") {
          state.roleModulePermissions = {
            clinicId:
              payload?.clinicId ??
              state.roleModulePermissions?.clinicId ??
              null,
            rolePermissions: Array.isArray(payload?.rolePermissions)
              ? payload.rolePermissions
              : state.roleModulePermissions?.rolePermissions || [],
          };
        }
      })
      .addCase(createRoleModulePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateRoleModulePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRoleModulePermissions.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload && typeof payload === "object") {
          state.roleModulePermissions = {
            clinicId:
              payload?.clinicId ??
              state.roleModulePermissions?.clinicId ??
              null,
            rolePermissions: Array.isArray(payload?.rolePermissions)
              ? payload.rolePermissions
              : state.roleModulePermissions?.rolePermissions || [],
          };
        }
      })
      .addCase(updateRoleModulePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApprovalRuleTiers } = globalSettingsSlice.actions;
export default globalSettingsSlice.reducer;
